import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable, Subject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  GenericException,
  UnAuthorizedException,
} from '../../../core/logic/exception';
import { DietitianId } from '../../dietitian/domain/dietitian';
import { SharedDocumentId } from '../../document/domain/shared_document/shared_document';
import { PatientId } from '../../patient/domain/patient';
import {
  ReportAiEvent,
  ReportAiEventDetail,
} from '../domain/events/report-ai-events';
import {
  Transcription,
  TranscriptionProps,
  TranscriptionReportProps,
  TranscriptionSegmentProps,
} from '../domain/transcription';
import { TranscriptionNotFoundException } from '../domain/transcription_exceptions';
import { TranscriptionUpload } from '../domain/transcription_upload';

export interface TranscriptionSegmentSchema {
  start: number;
  end: number;
  text: string;
  speaker: string;
}

export interface TranscriptionReportSchema {
  code: string;
  label: string;
  value: any;
  inputTokens: number | undefined;
  outputTokens: number | undefined;
}

export interface TranscriptionSchema {
  patientId: string;
  dietitianId: string;
  name: string;
  label: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  path: string;
  size: number;
  mimeType: string;
  audioDurationSeconds: number | null;
  processingDurationSeconds: number | null;
  status: string;
  segments: TranscriptionSegmentSchema[];
  segmentsAnonymized: TranscriptionSegmentSchema[];
  reports: TranscriptionReportSchema[] | null;
  sharedDocumentId: string | null;
  sharedAt: Timestamp | null;
}

@Injectable()
export class TranscriptionRepository {
  eventsSubject = new Subject<ReportAiEvent>();
  private readonly getTranscriptionAudioUrlCloud: (data: {
    transcriptionId: string;
  }) => Observable<{ url: string }>;
  private readonly transcriptionReportCloud: (data: {
    dietitianId: string;
    patientId: string;
    transcriptionId: string;
    templateId: string;
  }) => Observable<ReportAiEventDetail | undefined>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
    private storage: AngularFireStorage,
    private fireAuth: AngularFireAuth,
    private http: HttpClient,
  ) {
    this.transcriptionReportCloud = this.functions.httpsCallable<
      {
        dietitianId: string;
        patientId: string;
        transcriptionId: string;
        templateId: string;
      },
      any
    >('transcriber-transcriptionReport');

    this.getTranscriptionAudioUrlCloud = this.functions.httpsCallable<
      { transcriptionId: string },
      { url: string }
    >('transcriber-getTranscriptionAudioUrl');
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<TranscriptionSchema>(
      'transcriptions',
      queryFn,
    );
  }

  public toSchema(transcription: Transcription): TranscriptionSchema {
    return <TranscriptionSchema>{
      dietitianId: transcription.dietitianId.id.toString(),
      patientId: transcription.patientId.id.toString(),
      name: transcription.name,
      label: transcription.label,
      createdAt:
        transcription.createdAt !== undefined
          ? Timestamp.fromDate(transcription.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      path: transcription.path ?? null,
      size: transcription.size,
      mimeType: transcription.mimeType,
      audioDurationSeconds: transcription.audioDurationSeconds ?? null,
      processingDurationSeconds:
        transcription.processingDurationSeconds ?? null,
      status: transcription.status,
      segments: this.toSegmentSchema(transcription.segments),
      segmentsAnonymized: this.toSegmentSchema(
        transcription.segmentsAnonymized,
      ),
      reports: transcription.reports
        ? this.toReportsSchema(transcription.reports)
        : null,
      sharedDocumentId: transcription.sharedDocumentId?.id.toString() ?? null,
      sharedAt:
        transcription.sharedAt !== undefined
          ? Timestamp.fromDate(transcription.sharedAt)
          : null,
    };
  }

  public fromSchema(schema: TranscriptionSchema, id?: string): Transcription {
    return Transcription.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(schema.dietitianId)),
        patientId: PatientId.create(new UniqueEntityID(schema.patientId)),
        name: schema.name,
        label: schema.label,
        createdAt: schema.createdAt?.toDate(),
        updatedAt: schema.updatedAt?.toDate(),
        path: schema.path,
        size: schema.size ?? -1,
        mimeType: schema.mimeType ?? 'unknown',
        audioDurationSeconds: schema.audioDurationSeconds ?? undefined,
        processingDurationSeconds:
          schema.processingDurationSeconds ?? undefined,
        status: schema.status,
        segments: this.fromSegmentSchema(schema.segments),
        segmentsAnonymized: this.fromSegmentSchema(schema.segmentsAnonymized),
        reports: schema.reports
          ? this.fromReportsSchema(schema.reports)
          : undefined,
        sharedDocumentId: schema.sharedDocumentId
          ? SharedDocumentId.create(new UniqueEntityID(schema.sharedDocumentId))
          : undefined,
        sharedAt: schema.sharedAt?.toDate() ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  async generatePdf(transcriptionId: string, code: string): Promise<Blob> {
    const currentUser = await this.fireAuth.currentUser;
    const token = currentUser ? await currentUser.getIdToken() : undefined;
    if (!token) {
      throw new UnAuthorizedException();
    }

    let result: HttpResponse<Blob>;
    try {
      result = await firstValueFrom(
        this.http.post(
          environment.firebaseFunctionsUrl +
            'transcriber-generateTranscriptionReportPdf',
          {
            transcriptionId,
            code,
          },
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/pdf, application/json',
            }),
            observe: 'response',
            responseType: 'blob',
          },
        ),
      );
    } catch (error: unknown) {
      if (
        error instanceof HttpErrorResponse &&
        error.error instanceof Blob &&
        error.error.type == 'application/json'
      ) {
        const stJson = await error.error.text();
        const json = stJson
          ? JSON.parse(stJson)
          : { message: "une erreur s'est produite" };
        throw new GenericException(json.message);
      }
      throw new GenericException(error);
    }

    if (!result.ok) {
      throw new GenericException(result.statusText);
    }

    if (!result.body) {
      throw new GenericException();
    }

    if (result.body.type == 'application/pdf') {
      return result.body;
    }

    if (result.body.type == 'application/json') {
      const stJson = await result.body.text();
      const json = stJson
        ? JSON.parse(stJson)
        : { message: "une erreur s'est produite" };
      throw new GenericException(json.message);
    }

    throw new GenericException();
  }

  private toSegmentSchema(segments: TranscriptionSegmentSchema[] | undefined) {
    if (segments) {
      return segments.map((segment) => {
        return <TranscriptionSegmentSchema>{
          start: segment.start,
          end: segment.end,
          text: segment.text,
          speaker: segment.speaker,
        };
      });
    }
    return null;
  }

  private toReportSchema(
    report: TranscriptionReportProps,
  ): TranscriptionReportSchema {
    return <TranscriptionReportSchema>{
      code: report.code,
      label: report.label,
      value: report.value ?? undefined,
      inputTokens: report.inputTokens ?? undefined,
      outputTokens: report.outputTokens ?? undefined,
    };
  }

  private toReportsSchema(
    reports: TranscriptionReportProps[] | undefined,
  ): TranscriptionReportSchema[] | null {
    if (!reports) {
      return null;
    }
    return reports.map((r) => this.toReportSchema(r));
  }

  private fromReportSchema(
    report: TranscriptionReportSchema,
  ): TranscriptionReportProps {
    return <TranscriptionReportProps>{
      code: report.code,
      label: report.label,
      value: report.value ?? undefined,
      inputTokens: report.inputTokens ?? undefined,
      outputTokens: report.outputTokens ?? undefined,
    };
  }

  private fromSegmentSchema(segments: TranscriptionSegmentSchema[]) {
    if (segments) {
      return segments.map((segment) => {
        return <TranscriptionSegmentProps>{
          start: segment.start,
          end: segment.end,
          text: segment.text,
          speaker: segment.speaker,
        };
      });
    }
    return undefined;
  }

  async load(transcriptionId: string): Promise<Transcription> {
    const snap = await firstValueFrom(
      this.collection().doc(transcriptionId).get(),
    );
    if (!snap.exists || snap.data == null) {
      throw new TranscriptionNotFoundException();
    }

    return this.fromSchema(snap.data() as TranscriptionSchema, snap.id);
  }

  upload(transcription: Transcription): TranscriptionUpload {
    try {
      if (!transcription.path) {
        throw new Error("Le chemin de destination n'est pas déterminé !");
      }

      if (!transcription.file && !transcription.blob) {
        throw new Error("Aucun flux audio n'ait disponible !");
      }

      const fileRef = this.storage.ref(transcription.path);
      const task = fileRef.put(
        transcription.file ? transcription.file : transcription.blob,
        {
          contentType: transcription.mimeType,
        },
      );

      return TranscriptionUpload.create({
        transcription: transcription,
        task,
      });
    } catch (e) {
      console.error('upload transcription error', e);
      throw e;
    }
  }

  async create(
    transcription: Transcription,
    transcriptionId?: string | undefined,
  ): Promise<Transcription> {
    const schema = this.toSchema(transcription);
    if (transcriptionId) {
      await this.collection().doc(transcriptionId).set(schema);
      return this.fromSchema(schema, transcriptionId);
    } else {
      const ref = await this.collection().add(schema);
      return this.fromSchema(schema, ref.id);
    }
  }

  async save(transcription: Transcription): Promise<Transcription> {
    // check for existing document file
    const snap = await firstValueFrom(
      this.collection().doc(transcription.id.toString()).get(),
    );
    const path = snap.data()?.path;
    // delete old file
    if (path && path != transcription.path) {
      await this.deleteFile(path);
    }

    const schema = this.toSchema(transcription);
    return this.collection()
      .doc(transcription.transcriptionId.id.toString())
      .set(schema)
      .then(() =>
        this.fromSchema(
          schema,
          transcription.transcriptionId.id.toString(),
        ).copyWith({
          file: transcription.file,
        } as TranscriptionProps),
      );
  }

  async update(transcription: Transcription): Promise<Transcription> {
    const schema = this.toSchema(transcription);
    return this.collection()
      .doc(transcription.transcriptionId.id.toString())
      .set(schema)
      .then(() =>
        this.fromSchema(schema, transcription.transcriptionId.id.toString()),
      );
  }

  valueChanges(
    transcriptionId: string | undefined,
  ): Observable<TranscriptionSchema | undefined> {
    return this.collection().doc(transcriptionId).valueChanges();
  }

  async findAll(dietId: string, patientId: string, asc = false) {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref
          .where('dietitianId', '==', dietId)
          .where('patientId', '==', patientId)
          .orderBy('updatedAt', asc ? 'asc' : 'desc'),
      ).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async delete(transcriptionId: string) {
    return this.collection().doc(transcriptionId).delete();
  }

  private fromReportsSchema(
    reports: TranscriptionReportSchema[] | null,
  ): TranscriptionReportProps[] | undefined {
    if (!reports) {
      return undefined;
    }
    return reports.map((r) => this.fromReportSchema(r));
  }

  get events$(): Observable<ReportAiEvent> {
    return this.eventsSubject.asObservable();
  }

  dispatch(event: ReportAiEvent) {
    this.eventsSubject.next(event);
  }

  async transcriptionReport(
    dietitianId: string,
    patientId: string,
    transcriptionId: string,
    templateId: string,
  ): Promise<ReportAiEventDetail | undefined> {
    return await firstValueFrom(
      this.transcriptionReportCloud({
        dietitianId,
        patientId,
        transcriptionId,
        templateId,
      }),
    );
  }

  async getAudioUrl(transcriptionId: string): Promise<string> {
    const result = await firstValueFrom(
      this.getTranscriptionAudioUrlCloud({ transcriptionId }),
    );
    return result.url;
  }

  private async fileExists(path: string): Promise<boolean> {
    let url: string;
    try {
      url = await firstValueFrom(this.storage.ref(path).getDownloadURL());
    } catch (error) {
      return false;
    }
    return url != null;
  }

  private async deleteFile(path: string): Promise<void> {
    if (!(await this.fileExists(path))) return;

    await firstValueFrom(this.storage.ref(path).delete());
  }
}
